import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useScopedAuth } from "@/components/CurrentShopProvider";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { repository } from "@/infrastructure/repositories";
import type { Condition } from "@/domain/repositoryInterface/Repository";
import { shiftDeps } from "@/usecase/shift/deps";
import { bulkGenerateShifts } from "@/usecase/shift/bulkGenerateShifts";
import { shiftGateway } from "@/infrastructure/gateways";

export type StaffShiftRow = Tables<"staff_shifts">;
export type StaffWorkdayPatternRow = Tables<"staff_workday_patterns">;
export type StaffHolidayRow = Tables<"staff_holidays">;
export type ShopHoursRow = Tables<"shop_hours">;
export type ShopTimeSettingRow = Tables<"shop_time_settings">;

const shopHoursRepo = repository("shop_hours");
const workdayPatternRepo = repository("staff_workday_patterns");
const staffRepo = repository("staff");
const staffShiftRepo = repository("staff_shifts");
const staffHolidayRepo = repository("staff_holidays");
const shopTimeSettingRepo = repository("shop_time_settings");
const shiftTimePatternRepo = repository("shift_time_patterns");

// ===== Shop Hours (営業時間テンプレート) =====
export function useShopHours(shopId: string | undefined) {
  return useQuery<ShopHoursRow[]>({
    queryKey: ["shop_hours", shopId],
    enabled: !!shopId,
    queryFn: async () => {
      const { data } = await shopHoursRepo.findMany({
        where: [
          { column: "shop_id", op: "eq", value: shopId! },
          { column: "deleted_at", op: "isNull" },
        ],
        orderBy: [{ column: "sort_order" }],
      });
      return data;
    },
  });
}

// ===== Staff Workday Patterns (出勤パターン) =====
export function useUpsertWorkdayPattern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      staff_id: string;
      day_of_week: string;
      is_off?: boolean;
      start_time?: string | null;
      end_time?: string | null;
    }) => {
      const { data: existing } = await workdayPatternRepo.findMany({
        select: "id",
        where: [
          { column: "staff_id", op: "eq", value: params.staff_id },
          { column: "day_of_week", op: "eq", value: params.day_of_week },
        ],
      });

      const rec = {
        is_off: params.is_off ?? false,
        start_time: params.start_time ?? null,
        end_time: params.end_time ?? null,
      };

      if (existing[0]) {
        await workdayPatternRepo.update({ id: existing[0].id, patch: rec });
      } else {
        await workdayPatternRepo.create({
          staff_id: params.staff_id,
          day_of_week: params.day_of_week as any,
          ...rec,
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["staff_workday_patterns"] });
      qc.invalidateQueries({ queryKey: ["staff_workday_patterns_all"] });
    },
  });
}

export function useWorkdayPatterns(staffId: string | undefined) {
  return useQuery<StaffWorkdayPatternRow[]>({
    queryKey: ["staff_workday_patterns", staffId],
    enabled: !!staffId,
    queryFn: async () => {
      const { data } = await workdayPatternRepo.findMany({
        where: [{ column: "staff_id", op: "eq", value: staffId! }],
        orderBy: [{ column: "day_of_week" }],
      });
      return data;
    },
  });
}

export function useAllWorkdayPatterns() {
  const { authUser } = useScopedAuth();
  return useQuery<StaffWorkdayPatternRow[]>({
    queryKey: ["staff_workday_patterns_all", authUser?.shop_id, authUser?.company_id],
    // 自店舗スタッフの出勤パターンを staff!inner 結合で DB 側に絞る（旧: 全staff引き→staff_id IN）
    queryFn: () => shiftGateway.staffWorkdayPatterns(authUser?.shop_id ?? null),
  });
}

// ===== Staff Shifts (個別シフト) =====
export function useStaffShifts(yearMonth: string) {
  const { authUser } = useScopedAuth();
  // yearMonth = "2026-02"
  const startDate = `${yearMonth}-01`;
  const endDay = new Date(
    Number(yearMonth.slice(0, 4)),
    Number(yearMonth.slice(5, 7)),
    0
  ).getDate();
  const endDate = `${yearMonth}-${String(endDay).padStart(2, "0")}`;

  return useQuery<StaffShiftRow[]>({
    queryKey: ["staff_shifts", yearMonth, authUser?.shop_id],
    queryFn: async () => {
      const where: Condition[] = [
        { column: "date", op: "gte", value: startDate },
        { column: "date", op: "lte", value: endDate },
      ];
      if (authUser?.shop_id) {
        where.push({ column: "shop_id", op: "eq", value: authUser.shop_id });
      }

      const { data } = await staffShiftRepo.findMany({
        where,
        orderBy: [{ column: "date" }, { column: "staff_id" }],
      });
      return data;
    },
  });
}

export function useUpsertShift() {
  const qc = useQueryClient();
  const { authUser } = useScopedAuth();

  return useMutation({
    mutationFn: async (params: {
      staff_id: string;
      date: string;
      start_time?: string | null;
      end_time?: string | null;
      break_start?: string | null;
      break_end?: string | null;
      is_off?: boolean;
      memo?: string | null;
    }) => {
      const shopId = authUser?.shop_id;
      if (!shopId) throw new Error("店舗情報が設定されていません。管理者にお問い合わせください。");

      // Check if shift already exists for this staff+date
      const { data: existing } = await staffShiftRepo.findMany({
        select: "id",
        where: [
          { column: "staff_id", op: "eq", value: params.staff_id },
          { column: "date", op: "eq", value: params.date },
        ],
      });

      if (existing[0]) {
        await staffShiftRepo.update({
          id: existing[0].id,
          patch: {
            start_time: params.start_time ?? null,
            end_time: params.end_time ?? null,
            break_start: params.break_start ?? null,
            break_end: params.break_end ?? null,
            is_off: params.is_off ?? false,
            memo: params.memo ?? null,
          },
        });
      } else {
        await staffShiftRepo.create({
          staff_id: params.staff_id,
          date: params.date,
          shop_id: shopId,
          start_time: params.start_time ?? null,
          end_time: params.end_time ?? null,
          break_start: params.break_start ?? null,
          break_end: params.break_end ?? null,
          is_off: params.is_off ?? false,
          memo: params.memo ?? null,
        });
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff_shifts"] }),
  });
}

export function useBulkGenerateShifts() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (params: { yearMonth: string; staffIds: string[] }) =>
      bulkGenerateShifts(params, shiftDeps),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff_shifts"] }),
  });
}

// ===== Staff Holidays (休日) =====
export function useStaffHolidays(yearMonth: string) {
  const { authUser } = useScopedAuth();
  const startDate = `${yearMonth}-01`;
  const endDay = new Date(
    Number(yearMonth.slice(0, 4)),
    Number(yearMonth.slice(5, 7)),
    0
  ).getDate();
  const endDate = `${yearMonth}-${String(endDay).padStart(2, "0")}`;

  return useQuery<StaffHolidayRow[]>({
    queryKey: ["staff_holidays", yearMonth, authUser?.shop_id],
    // 自店舗スタッフの休日を staff!inner 結合で DB 側に絞る（旧: staff引き→staff_id IN）
    queryFn: () => shiftGateway.staffHolidays(authUser?.shop_id ?? "", startDate, endDate),
  });
}

export function useCreateStaffHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { staff_id: string; date: string; reason?: string | null }) => {
      await staffHolidayRepo.create({
        staff_id: params.staff_id,
        date: params.date,
        reason: params.reason ?? null,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff_holidays"] }),
  });
}

export function useDeleteStaffHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await staffHolidayRepo.delete([id]);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff_holidays"] }),
  });
}

// ===== Shop Hours CRUD =====
type DayOfWeek = NonNullable<ShopHoursRow["day_of_week"]>;

export function useUpsertShopHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      shop_id: string;
      day_of_week: string;
      open_time?: string | null;
      close_time?: string | null;
      is_holiday?: boolean;
    }) => {
      const { data: existing } = await shopHoursRepo.findMany({
        select: "id",
        where: [
          { column: "shop_id", op: "eq", value: params.shop_id },
          { column: "day_of_week", op: "eq", value: params.day_of_week as DayOfWeek },
          { column: "deleted_at", op: "isNull" },
        ],
      });

      if (existing[0]) {
        await shopHoursRepo.update({
          id: existing[0].id,
          patch: {
            open_time: params.open_time ?? null,
            close_time: params.close_time ?? null,
            is_holiday: params.is_holiday ?? false,
          },
        });
      } else {
        await shopHoursRepo.create({
          shop_id: params.shop_id,
          day_of_week: params.day_of_week as DayOfWeek,
          open_time: params.open_time ?? null,
          close_time: params.close_time ?? null,
          is_holiday: params.is_holiday ?? false,
        });
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shop_hours"] }),
  });
}

// ===== Shop Time Settings (key-value) =====
export function useShopTimeSettings(shopId: string | undefined) {
  return useQuery<ShopTimeSettingRow[]>({
    queryKey: ["shop_time_settings", shopId],
    enabled: !!shopId,
    queryFn: async () => {
      const { data } = await shopTimeSettingRepo.findMany({
        where: [{ column: "shop_id", op: "eq", value: shopId! }],
      });
      return data;
    },
  });
}

export function useUpsertShopTimeSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { shop_id: string; setting_key: string; setting_value: string | null }) => {
      const { data: existing } = await shopTimeSettingRepo.findMany({
        select: "id",
        where: [
          { column: "shop_id", op: "eq", value: params.shop_id },
          { column: "setting_key", op: "eq", value: params.setting_key },
        ],
      });

      if (existing[0]) {
        await shopTimeSettingRepo.update({
          id: existing[0].id,
          patch: { setting_value: params.setting_value },
        });
      } else {
        await shopTimeSettingRepo.create({
          shop_id: params.shop_id,
          setting_key: params.setting_key,
          setting_value: params.setting_value,
        });
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shop_time_settings"] }),
  });
}

// ===== Shift Time Patterns (シフト編集用時間プリセット) =====
export type ShopHourPatternRow = Tables<"shift_time_patterns">;

export function useShopHourPatterns(shopId: string | undefined) {
  return useQuery<ShopHourPatternRow[]>({
    queryKey: ["shift_time_patterns", shopId],
    enabled: !!shopId,
    queryFn: async () => {
      const { data } = await shiftTimePatternRepo.findMany({
        where: [
          { column: "shop_id", op: "eq", value: shopId! },
          { column: "deleted_at", op: "isNull" },
        ],
        orderBy: [{ column: "sort_order" }],
      });
      return data as ShopHourPatternRow[];
    },
  });
}

export function useSaveShopHourPatterns() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      rows: Array<{
        id: string;
        shop_id: string;
        sort_order: number | null;
        pattern_name: string;
        open_time: string | null;
        close_time: string | null;
        pattern_value: number | null;
        isNew?: boolean;
        markedForDelete?: boolean;
      }>
    ) => {
      for (const row of rows) {
        if (row.markedForDelete && !row.isNew) {
          await shiftTimePatternRepo.update({
            id: row.id,
            patch: { deleted_at: new Date().toISOString() } as TablesUpdate<"shift_time_patterns">,
          });
        } else if (!row.markedForDelete) {
          const rec: TablesInsert<"shift_time_patterns"> = {
            shop_id: row.shop_id,
            sort_order: row.sort_order,
            pattern_name: row.pattern_name,
            open_time: row.open_time || null,
            close_time: row.close_time || null,
            pattern_value: row.pattern_value,
          };
          if (row.isNew) {
            await shiftTimePatternRepo.create(rec);
          } else {
            await shiftTimePatternRepo.update({
              id: row.id,
              patch: rec as TablesUpdate<"shift_time_patterns">,
            });
          }
        }
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shift_time_patterns"] }),
  });
}
